import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import authenticateService from '@/apis/authenticate.service';
import User from '@/dto/users/User.dto';
import { UserPagingRequest } from '@/dto/commons/PaginationRequest.dto';
import UpdateUserDto from '@/dto/users/UpdateUser.dto';
import CreateUserDto from '@/dto/users/CreateUser.dto';
import { CheckoutInfo } from '@/dto/payment/CheckoutInfo.dto';
import { SelectOption } from '@/dto/commons/SelectOption.dto';
import { handleDebounce } from '@utils/time.util';
import userService from '@/apis/user.service';

export default class UserStore {
  users: User[];

  currentUser: any = null;
  totalCount: number;
  braintreeTokenId = '';

  isLoading = false;

  initModel: User = {
    email: '',
    fullName: '',
    isVerified: false,
    password: '',
    role: null,
  };

  timeOut: NodeJS.Timeout | null = null;

  constructor() {
    this.users = [];
    this.totalCount = 0;
    makeObservable(this, {
      users: observable,
      currentUser: observable,
      totalCount: observable,
      braintreeTokenId: observable,
      isLoading: observable,
      setUsers: action,
      setTotalCount: action,
      setCurrentUser: action,
      setBraintreeTokenId: action,
      debounceSearchUser: action,
      setLoading: action,
      userOptions: computed,
    });
  }

  async register(model: CreateUserDto) {
    return await authenticateService.register(model);
  }

  async list(model: UserPagingRequest, isMinified = false) {
    this.setLoading(true);
    const { data, count } = isMinified
      ? await userService.minified(model)
      : await userService.list(model);
    this.setUsers(data);
    this.setTotalCount(count);
    this.setLoading(false);
  }

  setUsers(data: any[]) {
    this.users = data;
  }

  setCurrentUser(data: any) {
    this.currentUser = data;
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  async setTotalCount(count: any) {
    this.totalCount = count;
  }

  async create(model: CreateUserDto) {
    return await userService.create(model);
  }

  async update(model: UpdateUserDto) {
    return await userService.update(+model.id, model);
  }

  async verify(id: number) {
    return await userService.verify(id);
  }

  async unVerify(id: number) {
    return await userService.unVerify(id);
  }

  async restore(id: number) {
    return await userService.restore(id);
  }

  async delete(id: number) {
    return await userService.delete(id);
  }

  async hardDelete(id: number) {
    return await userService.hardDelete(id);
  }

  async getUserDetail(id: number) {
    const user = await userService.getUserDetail(id);
    this.setCurrentUser(user);
  }

  unsetCurrentUser() {
    this.setCurrentUser(null);
  }

  setBraintreeTokenId(data: any) {
    this.braintreeTokenId = data;
  }

  async genBraintreeToken() {
    const result = await userService.genBraintreeToken();
    this.setBraintreeTokenId(result.clientToken);
  }

  async sendClientNonce(model: CheckoutInfo) {
    await userService.sendClientNonce(model);
  }

  get userOptions(): SelectOption[] {
    return this.users.map((c: User) => ({
      label: `${c.fullName} (${c.email})`,
      value: c.id || 0,
    }));
  }

  debounceSearchUser(search: string) {
    handleDebounce(
      this.timeOut,
      () => this.list({ search }, true),
      500,
      (value: NodeJS.Timeout) => this.setCustomTimeout(value)
    );
  }

  setCustomTimeout(value: NodeJS.Timeout) {
    this.timeOut = value;
  }
}

export const UserStoreContext = React.createContext(new UserStore());
